import Link from 'next/link';
import { useCallback, useMemo, useState } from 'react';
import { isEqual, noop, toInteger } from 'lodash';
import Pagination from '../../../../base-components/Pagination';
import { SvgIcon } from '../../../../base-components/SvgIcon';
import { PlannedPosition } from '../../../../types/entities';
import boolToTextFilter from '../../../../utils/boolToTextFilter';
import formatMomentDate from '../../../../utils/formatMomentDate';
import toMskMoment from '../../../../utils/toMskMoment';
import { PageHeader } from '../../components/PageHeader';
import styles from './PlannedPurchasePosition.module.scss';
import { useEffectOnce, useUpdateEffect } from 'react-use';
import { useRouter } from 'next/router';

export type PlannedPurchasePositionState = {
    limit?: number;
    page?: number;
};
export type QueryParams = Record<
    keyof PlannedPurchasePositionState,
    string | string[] | undefined
>;

const ArrowLeft = () => (
    <SvgIcon width={8} height={14} noFill>
        <path
            d="M7 1 1 7l6 6"
            strokeLinecap="round"
            strokeLinejoin="round"
            // stroke={color}
        />
    </SvgIcon>
);

const encodeStateParams = (
    state: PlannedPurchasePositionState
): QueryParams => ({
    limit: String(state.limit || 100),
    page: String(state.page || 1),
});

const decodeStateParams = (
    query: QueryParams
): PlannedPurchasePositionState => ({
    limit: Math.max(toInteger(query.page), 100),
    page: Math.max(toInteger(query.page), 1),
});

export type PlannedPurchasePositionProps = {
    position: PlannedPosition;
    total: number;
};

export const PlannedPurchasePosition: React.FC<
    PlannedPurchasePositionProps
> = ({ position, total }) => {
    const router = useRouter();
    const [state, setState] = useState<PlannedPurchasePositionState>({});
    const hasSubjects = useMemo(
        () => Boolean(position.subjects?.length),
        [position]
    );
    /*
     * Обновляем параметры роутера если они отличаются от параметров фильтра
     */
    useUpdateEffect(() => {
        if (router.isReady) {
            const newEncodedState = encodeStateParams(state);
            const decodedState = decodeStateParams(router.query as QueryParams);
            const currentEncodedState = {
                ...newEncodedState,
                ...encodeStateParams(decodedState),
            };
            if (!isEqual(currentEncodedState, newEncodedState)) {
                const href = {
                    pathname: `/planned/${router.query?.slug}`,
                    query: newEncodedState,
                };
                router.push(href).catch(noop);
            }
        }
    }, [state, router.query, router.isReady]);

    /*
     * При первом запуске инициализируем состояние фильтра параметрами из роутера
     */
    useEffectOnce(() => {
        const decodedState = decodeStateParams(router.query as QueryParams);
        setState(decodedState);
        if (total > 10_000) {
            setState({
                ...decodedState,
                limit: Math.ceil(total / 100),
            });
        }
    });

    const listElement = useCallback((title: string | any, definition: any) => {
        if (!title) {
            return null;
        }
        return (
            <>
                <dt key={title}>{title}</dt>
                <dd key={definition}>
                    {definition === null ||
                    definition === undefined ||
                    definition === ''
                        ? '-'
                        : definition}
                </dd>
            </>
        );
    }, []);
    const getDate = useCallback(
        (date: string | null, isDatePlanned?: boolean) => {
            const format = isDatePlanned === false ? 'month' : 'date';
            return date
                ? formatMomentDate(toMskMoment(date), format)
                : 'Не указано';
        },
        []
    );
    const onPaginationChange = useCallback(
        (page: number): void => {
            setState({
                ...state,
                page,
            });
        },
        [state]
    );
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <PageHeader>{`Позиция № ${position.ordNumber}`}</PageHeader>
                <Link href={`/planned-purchases?sectionOrg=${position.orgId}`}>
                    <a className={styles.link}>
                        <ArrowLeft />
                        {'назад к планируемым закупкам'}
                    </a>
                </Link>
            </div>
            <div className={styles.card}>
                <h2>{position.title}</h2>
                <h3>Общая информация</h3>
                <dl className={styles.mainInfo}>
                    {listElement('Заказчик', position?.organization?.name)}
                    {listElement('ИНН заказчика', position?.organization?.inn)}
                    {listElement(
                        'Закупка у СМП',
                        boolToTextFilter(position?.isSmp)
                    )}
                    {listElement(
                        'Способ закупки',
                        position?.procedureConf?.title
                    )}
                    {listElement(
                        'Минимальные необходимые требования к товарам (услугам)',
                        position?.minRequirements
                    )}
                    {listElement(
                        'Планируемая дата размещения',
                        getDate(
                            position?.dateAnnouncePub,
                            position?.isDatePlanned
                        )
                    )}
                    {listElement(
                        'НМЦ включает финансирование за счёт бюджетных средств',
                        boolToTextFilter(
                            position?.isMaximumContractPriceFromBudget
                        )
                    )}
                    {listElement(
                        'НМЦ',
                        [
                            position?.startPrice,
                            position?.currency?.characterCode,
                        ].join(' ')
                    )}
                    {listElement(
                        'Срок исполнения договора',
                        getDate(position?.dateExec, position?.isDatePlanned)
                    )}
                    {listElement(
                        'Регион поставки (ОКАТО)',
                        `${position?.fiasRegion?.okato} (${position?.fiasRegion?.offName} ${position?.fiasRegion?.shortName})`
                    )}
                </dl>
                {hasSubjects && <h3>Информация о товарах, работах, услугах</h3>}
                {hasSubjects && (
                    <div className={styles.subjects}>
                        <div className={styles.hat}>
                            <div>№</div>
                            <div>ОКПД2</div>
                            <div>Количество</div>
                        </div>
                        {position.subjects?.map((subject, idx) => {
                            return (
                                <div
                                    className={styles.sub}
                                    key={`${subject.id}-${idx}`}
                                >
                                    <div>{subject.ordNumber}</div>
                                    <div>
                                        <div>{`${subject.okpd2?.code} ${subject.okpd2?.name}`}</div>
                                        {Boolean(
                                            subject.additionalInfo.length
                                        ) && (
                                            <div className={styles.additional}>
                                                <span>
                                                    Дополнительная информация:
                                                </span>
                                                <span>
                                                    {subject.additionalInfo}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div>{`${subject.quantity} ${
                                        subject.okei?.symbol ?? ''
                                    }`}</div>
                                </div>
                            );
                        })}
                        {total > 1 && ( //
                            <div className={styles.pagination}>
                                <Pagination
                                    page={state?.page || 1}
                                    total={total}
                                    limit={state?.limit || 100}
                                    onChange={onPaginationChange}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
